import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { X as CloseIcon, Search } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Command — Cmd-K style command palette / quick search.
 *
 * Built on the `cmdk` library which handles fuzzy filtering, keyboard
 * navigation, and accessible roles. We wrap its primitives with v2 styles
 * so the palette feels consistent with the rest of the design system.
 *
 * Use `Command` for inline filterable lists (e.g. an autocomplete picker
 * inside a popover). Use `CommandDialog` when the palette should open as
 * a full-screen overlay (the classic ⌘K / Ctrl-K experience).
 */
export const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(function Command({ className, ...props }, ref) {
  return <CommandPrimitive ref={ref} className={cx("cw-v2-command", className)} {...props} />;
});
Command.displayName = "Command";

export const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(function CommandInput({ className, ...props }, ref) {
  return (
    <div className="cw-v2-command__input-wrap" cmdk-input-wrapper="">
      <Search className="cw-v2-command__input-icon" aria-hidden />
      <CommandPrimitive.Input
        ref={ref}
        className={cx("cw-v2-command__input", className)}
        {...props}
      />
    </div>
  );
});
CommandInput.displayName = "CommandInput";

export const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(function CommandList({ className, ...props }, ref) {
  return (
    <CommandPrimitive.List ref={ref} className={cx("cw-v2-command__list", className)} {...props} />
  );
});
CommandList.displayName = "CommandList";

export const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(function CommandEmpty({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className={cx("cw-v2-command__empty", className)}
      {...props}
    />
  );
});
CommandEmpty.displayName = "CommandEmpty";

export const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(function CommandGroup({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cx("cw-v2-command__group", className)}
      {...props}
    />
  );
});
CommandGroup.displayName = "CommandGroup";

export const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(function CommandSeparator({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      className={cx("cw-v2-command__separator", className)}
      {...props}
    />
  );
});
CommandSeparator.displayName = "CommandSeparator";

export const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(function CommandItem({ className, ...props }, ref) {
  return (
    <CommandPrimitive.Item ref={ref} className={cx("cw-v2-command__item", className)} {...props} />
  );
});
CommandItem.displayName = "CommandItem";

export function CommandShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cx("cw-v2-command__shortcut", className)} {...props} />;
}

/* ─── Dialog variant ─── */

export interface CommandDialogProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  /** Accessible title for the dialog. Required by Radix. */
  title?: string;
  /** Hide the visible title (still rendered for SR via VisuallyHidden). */
  hideTitle?: boolean;
  /** Optional description for screen readers. */
  description?: string;
  /** Inner command children (Input, List, Group, Items, …). */
  children?: React.ReactNode;
}

export function CommandDialog({
  children,
  description,
  hideTitle = true,
  title = "Command palette",
  ...props
}: CommandDialogProps) {
  return (
    <DialogPrimitive.Root {...props}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cw-v2-dialog__overlay" />
        <DialogPrimitive.Content className="cw-v2-theme cw-v2-command__dialog">
          {hideTitle ? (
            <DialogPrimitive.Title className="cw-v2-command__sr-only">
              {title}
            </DialogPrimitive.Title>
          ) : (
            <DialogPrimitive.Title className="cw-v2-command__title">{title}</DialogPrimitive.Title>
          )}
          {description ? (
            <DialogPrimitive.Description className="cw-v2-command__sr-only">
              {description}
            </DialogPrimitive.Description>
          ) : null}
          <DialogPrimitive.Close
            aria-label="Close command palette"
            className="cw-v2-command__close"
          >
            <CloseIcon className="cw-v2-command__close-icon" aria-hidden />
          </DialogPrimitive.Close>
          <Command>{children}</Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
